import _ from "lodash";

import { getPurePhoneNumber, getValueOrNull } from "../../../../../../utils/misc";
import { FORM_FIELD_TYPES, OTHER, PhoneNumberType } from "../../../constants";
import { cleanUnspecifiedValue } from "./cleanUnspecifiedValue";
import { getPreferredLanguage } from "./getPreferredLanguage";
import { evaluateInitialValueCondition } from "../../../utils/formConditions";

const INSURANCE = "insurance";

const getIsSelectOther = (section, field, form) => {
  if (field.alternativeFields) {
    const hasData = field.alternativeFields.some((alternativeField) => {
      return !!section?.[[alternativeField]];
    });
    if (hasData) {
      return OTHER;
    }
  }
  return getDefaultValueIfNeeded(field, form);
};

const getDefaultValueIfNeeded = (field, formData) => {
  if (field.conditions?.defaultValue) {
    const defaultValue = field.conditions.defaultValue;
    const shouldUseDefaultValue = evaluateInitialValueCondition(defaultValue, formData);
    if (shouldUseDefaultValue) {
      return field.defaultValue ?? null;
    }
    return null;
  }
  return field.defaultValue ?? null;
};

const fieldManipulations = {
  [FORM_FIELD_TYPES.PHONE]: ({ value, sectionObj, section, field, formData }) => {
    sectionObj.phoneType = section?.phoneType || PhoneNumberType.MOBILE;
    if (!value) return getDefaultValueIfNeeded(field, formData);

    return getPurePhoneNumber(value);
  },

  [FORM_FIELD_TYPES.LANGUAGE]: ({ value, field, formData }) => {
    if (!value) return getDefaultValueIfNeeded(field, formData);

    return getPreferredLanguage(value);
  },

  [FORM_FIELD_TYPES.STATE]: ({ field, formData, value }) => {
    if (!value) return getDefaultValueIfNeeded(field, formData);

    return getValueOrNull(value);
  },

  [FORM_FIELD_TYPES.INPUT]: ({ value, sectionName, field, formData }) => {
    if (!value) return getDefaultValueIfNeeded(field, formData);

    if (sectionName.includes(INSURANCE)) {
      return cleanUnspecifiedValue(value);
    }

    return value;
  },

  [FORM_FIELD_TYPES.SINGLE_SELECT]: ({ value, section, field, formData }) => {
    if (value) return value;

    return getIsSelectOther(section, field, formData);
  },

  [FORM_FIELD_TYPES.GENDER]: ({ value, field }) => {
    const dedicatedFieldProps = field && field.dedicatedFieldProps ? field.dedicatedFieldProps : {};
    const baseValues = dedicatedFieldProps.baseValues || ["female", "male"];
    const additionalOptions = dedicatedFieldProps.additionalOptions || [];
    const showThirdOption = dedicatedFieldProps.showThirdOption !== false; // default to true

    const validGenders = new Set(baseValues);
    if (showThirdOption) {
      validGenders.add("other");
    }
    additionalOptions.forEach((option) => {
      if (option.value) {
        validGenders.add(option.value);
      }
    });

    return validGenders.has(value) ? value : undefined;
  },

  [FORM_FIELD_TYPES.CHECKBOX]: ({ value, field, formData }) => {
    if (!value) return getDefaultValueIfNeeded(field, formData);
    return value;
  },
  [FORM_FIELD_TYPES.RADIO]: ({ value, field, formData }) => {
    if (!value) return getDefaultValueIfNeeded(field, formData);
    return value;
  },

  [FORM_FIELD_TYPES.RADIO_CAREGIVER]: ({ value, field, formData }) => {
    if (!value) {
      return getDefaultValueIfNeeded(field, formData);
    }
    return value;
  }
};

const getSectionOrSubSectionIfExists = (field, sourceValues) =>
  field.subSectionName ? sourceValues?.[field.subSectionName] : sourceValues;

const manipulateField = (field, sectionName, acc, sectionValues, formData) => {
  let sectionObj = getSectionOrSubSectionIfExists(field, acc[sectionName]);
  if (!sectionObj) {
    acc[sectionName][field.subSectionName] = {};
    sectionObj = acc[sectionName][field.subSectionName];
  }

  const section = getSectionOrSubSectionIfExists(field, sectionValues);
  const value = section?.[field.name];

  let manipulatedValue = value;
  const fieldManipulation = fieldManipulations[field.type];
  if (fieldManipulation) {
    manipulatedValue = fieldManipulation({ value, sectionObj, section, sectionName, field, formData });
  }

  sectionObj[field.name] = manipulatedValue;
};

// Some of the fields need to be manipulated for initial values.
// This is a generic manipulation for all fields.
// if a specific manipulation is needed - please add it in the specific EE initial values file.
export const getGenericFormInitialValues = (applicationForm, formJson) => {
  const formData = applicationForm?.formData;
  const initialValues = formJson.formSections.reduce((acc, section) => {
    const sectionName = section.sectionName;
    acc[sectionName] = {};
    section.rows.forEach((row) => {
      row.fields.forEach((field) => {
        if (field.type === FORM_FIELD_TYPES.MULTIPLIER) {
          field.dedicatedFieldProps.subFields.rows.forEach((subRow) => {
            subRow.fields.forEach((subField) => {
              field.dedicatedFieldProps.fieldIterationNames.map((iterationName) => {
                subField.subSectionName = _.camelCase(iterationName);
                manipulateField(subField, sectionName, acc, formData?.[sectionName], formData);
              });
            });
          });
        } else {
          manipulateField(field, sectionName, acc, formData?.[sectionName], formData);
        }
      });
    });
    return acc;
  }, {});

  return initialValues;
};
